import React, { useState, useEffect } from 'react';
import { 
  LaneType, 
  LaneTypeFormData,
  Checkpoint,
  Terminal
} from '../../types';
import { useEnhancedApi } from '../../hooks/useEnhancedApi';
import { useToast } from '../ui/Toast';
import { permissionChecks } from '../../lib/auth/helpers';

interface LaneTypeFormState {
  checkpoint_id: number;
  type_name: string;
  description: string;
  purpose: string;
}

export const LaneTypesManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<LaneType | null>(null);
  
  // Local state for dropdowns as fallback
  const [localTerminals, setLocalTerminals] = useState<Terminal[]>([]);
  const [localCheckpoints, setLocalCheckpoints] = useState<Checkpoint[]>([]);
  
  // Use authenticated API service
  const enhancedApi = useEnhancedApi();
  
  // Store data
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [laneTypes, setLaneTypes] = useState<LaneType[]>([]);
  
  // Mock store functions for compatibility
  const fetchTerminals = async (forceRefresh = false) => {
    const data = await enhancedApi.getTerminals(forceRefresh);
    setTerminals(data);
    return data;
  };
  
  const fetchCheckpoints = async (terminalId?: number, forceRefresh = false) => {
    const data = await enhancedApi.getCheckpoints(terminalId, forceRefresh);
    setCheckpoints(data);
    return data;
  };
  
  const fetchLaneTypes = async (checkpointId?: number, forceRefresh = false) => {
    try {
      const data: any = await enhancedApi.getLaneTypes(checkpointId, undefined, forceRefresh);
      
      // Handle the response format we're actually getting
      let laneTypesArray: LaneType[] = [];
      
      if (Array.isArray(data)) {
        laneTypesArray = data;
      } else if (data && data.lane_types && Array.isArray(data.lane_types)) {
        laneTypesArray = data.lane_types;
      } else if (data && data.data && Array.isArray(data.data.lane_types)) {
        laneTypesArray = data.data.lane_types;
      } else {
        laneTypesArray = [];
      }
      
      setLaneTypes(laneTypesArray);
      return laneTypesArray;
    } catch (error) {
      console.error('Error fetching lane types:', error);
      setLaneTypes([]);
      throw error;
    }
  };
  
  const updateLaneType = (updatedLaneType: LaneType) => {
    setLaneTypes(prev => prev.map(lt => 
      lt.lane_type_id === updatedLaneType.lane_type_id ? updatedLaneType : lt
    ));
  };
  
  const addLaneType = (newLaneType: LaneType) => {
    setLaneTypes(prev => [...prev, newLaneType]);
  };
  
  const { showToast } = useToast();

  // Form data states
  const [laneTypeFormData, setLaneTypeFormData] = useState<LaneTypeFormState>({
    checkpoint_id: 0,
    type_name: '',
    description: '',
    purpose: 'APP'
  });

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Selected filters
  const [selectedTerminal, setSelectedTerminal] = useState<number>(0);
  const [selectedPurpose, setSelectedPurpose] = useState<string>('all');

  // Use local data as fallback if store data is empty
  const availableTerminals = Array.isArray(terminals) && terminals.length > 0 ? terminals : Array.isArray(localTerminals) ? localTerminals : [];
  const availableCheckpoints = Array.isArray(checkpoints) && checkpoints.length > 0 ? checkpoints : Array.isArray(localCheckpoints) ? localCheckpoints : [];

  useEffect(() => {
    loadInitialData();
  }, []); // Empty dependency array to run only once

  useEffect(() => {
    if (selectedTerminal) {
      fetchCheckpoints(selectedTerminal);
    }
  }, [selectedTerminal]); // Only depend on selectedTerminal

  // Debug effect to monitor lane types changes
  useEffect(() => {
            // Lane types updated
  }, [laneTypes.length]); // Only depend on length to avoid infinite loops

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await fetchTerminals(true);
      await fetchCheckpoints(undefined, true);
      await fetchLaneTypes(undefined, true);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLaneTypeInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setLaneTypeFormData(prev => ({ 
      ...prev, 
      [name]: name === 'checkpoint_id' ? parseInt(value) : value 
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateLaneTypeForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!laneTypeFormData.checkpoint_id) {
      errors.checkpoint_id = 'Please select a checkpoint';
    }
    
    if (!laneTypeFormData.type_name.trim()) {
      errors.type_name = 'Lane type name is required';
    } else if (laneTypeFormData.type_name.trim().length < 2) {
      errors.type_name = 'Lane type name must be at least 2 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLaneTypeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permissionChecks.canUpdateQueueTypes()) {
      showToast('You do not have permission to create or update lane types', 'error');
      return;
    }
    if (!validateLaneTypeForm()) return;
    
    setLoading(true);
    try {
      if (editingItem) {
        // Update existing lane type
        const updatedLaneType = await enhancedApi.updateLaneType(editingItem.lane_type_id, {
          checkpoint_id: laneTypeFormData.checkpoint_id,
          type_name: laneTypeFormData.type_name,
          description: laneTypeFormData.description.trim() || undefined,
          purpose: laneTypeFormData.purpose.trim() || undefined
        });
        updateLaneType(updatedLaneType);
        showToast('Lane type updated successfully!', 'success');
      } else {
        // Create new lane type
        const newLaneType = await enhancedApi.createLaneType({
          checkpoint_id: laneTypeFormData.checkpoint_id,
          type_name: laneTypeFormData.type_name,
          description: laneTypeFormData.description.trim() || undefined,
          purpose: laneTypeFormData.purpose.trim() || undefined
        });
        addLaneType(newLaneType);
        showToast('Lane type created successfully!', 'success');
      }
      
      resetLaneTypeForm();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save lane type';
      setFormErrors({ submit: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditLaneType = (laneType: LaneType) => {
    if (!permissionChecks.canUpdateQueueTypes()) {
      showToast('You do not have permission to edit lane types', 'error');
      return;
    }
    setEditingItem(laneType);
    setLaneTypeFormData({
      checkpoint_id: laneType.checkpoint_id,
      type_name: laneType.type_name,
      description: laneType.description || '',
      purpose: laneType.purpose || ''
    });
    setShowAddForm(true);
    setFormErrors({});
  };

  const resetLaneTypeForm = () => {
    setLaneTypeFormData({
      checkpoint_id: 0,
      type_name: '',
      description: '',
      purpose: 'APP'
    });
    setFormErrors({});
    setShowAddForm(false);
    setEditingItem(null);
  };

  // Helper functions
  const getCheckpointName = (checkpointId: number) => {
    if (!Array.isArray(checkpoints)) return 'Unknown';
    return checkpoints.find((c: Checkpoint) => c.checkpoint_id === checkpointId)?.checkpoint_name || 'Unknown';
  };

  // Get unique purposes for filter dropdown
  const getUniquePurposes = () => {
    if (!Array.isArray(laneTypes)) return [];
    
    const purposes = laneTypes
      .map((lt: LaneType) => lt.purpose)
      .filter((purpose): purpose is string => purpose !== null && purpose !== undefined && purpose.trim() !== '')
      .filter((purpose: string, index: number, array: string[]) => array.indexOf(purpose) === index)
      .sort();
    return purposes;
  };

  // Filter lane types based on selected filters
  const getFilteredLaneTypes = () => {
    if (!Array.isArray(laneTypes)) return [];
    
    return laneTypes.filter((lt: LaneType) => {
      if (!lt.is_active) return false;
      
      // Filter by purpose
      if (selectedPurpose !== 'all') {
        if (selectedPurpose === 'none') {
          // Show items with no purpose (null, undefined, or empty)
          if (lt.purpose && lt.purpose.trim() !== '') return false;
        } else {
          // Show items with specific purpose
          if (lt.purpose !== selectedPurpose) return false;
        }
      }
      
      return true;
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Lane Types</h1>
          <p className="text-gray-400 text-sm">Manage lane types and configurations</p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-gray-800 rounded-xl p-4">
        <h3 className="text-lg font-semibold mb-4">Filter Options</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Purpose</label>
            <select
              value={selectedPurpose}
              onChange={(e) => setSelectedPurpose(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Purposes</option>
              <option value="none">No Purpose</option>
              {getUniquePurposes().map((purpose: string) => (
                <option key={purpose} value={purpose}>
                  {purpose}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setSelectedPurpose('all')}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded transition-colors text-sm font-medium"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Add Lane Type Button */}
      <div className="flex justify-between items-center">
        <button
          onClick={async () => {
            setLoading(true);
            try {
              await fetchLaneTypes(undefined, true);
            } catch (error) {
              console.error('Manual refresh failed:', error);
            } finally {
              setLoading(false);
            }
          }}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded transition-colors text-sm font-medium"
        >
          Refresh Data
        </button>
        {permissionChecks.canUpdateQueueTypes() && (
          <button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors text-sm font-medium"
          >
            {showAddForm ? 'Cancel' : 'Add Lane Type'}
          </button>
        )}
      </div>

      {/* Add/Edit Lane Type Form */}
      {showAddForm && (
        <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem ? 'Edit Lane Type' : 'Add New Lane Type'}
          </h3>
          <form onSubmit={handleLaneTypeSubmit} className="space-y-4">
            {/* Terminal Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Terminal <span className="text-red-400">*</span>
              </label>
              <select
                name="terminal_helper"
                value={selectedTerminal}
                onChange={(e) => {
                  setSelectedTerminal(parseInt(e.target.value));
                  setLaneTypeFormData(prev => ({ ...prev, checkpoint_id: 0 }));
                }}
                className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
                <option value={0}>Select a terminal</option>
                {availableTerminals.filter((t: Terminal) => t.is_active).map((terminal: Terminal) => {
                  // Rendering terminal
                  return (
                    <option key={terminal.terminal_id} value={terminal.terminal_id}>
                      {terminal.terminal_name}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Checkpoint Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Checkpoint <span className="text-red-400">*</span>
              </label>
              <select
                name="checkpoint_id"
                value={laneTypeFormData.checkpoint_id}
                onChange={handleLaneTypeInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.checkpoint_id ? 'border-red-500' : 'border-gray-600'
                }`}
                disabled={loading || !selectedTerminal}
              >
                <option value={0}>Select a checkpoint</option>
                {availableCheckpoints
                  .filter((c: Checkpoint) => c.terminal_id === selectedTerminal && c.is_active)
                  .map((checkpoint: Checkpoint) => {
                    // Rendering checkpoint for terminal
                    return (
                      <option key={checkpoint.checkpoint_id} value={checkpoint.checkpoint_id}>
                        {checkpoint.checkpoint_name}
                      </option>
                    );
                  })}
              </select>
              {formErrors.checkpoint_id && (
                <p className="mt-1 text-sm text-red-400">{formErrors.checkpoint_id}</p>
              )}
            </div>

            {/* Lane Type Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Lane Type Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="type_name"
                value={laneTypeFormData.type_name}
                onChange={handleLaneTypeInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.type_name ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter lane type name (e.g., Standard, Express, Family)"
                disabled={loading}
              />
              {formErrors.type_name && (
                <p className="mt-1 text-sm text-red-400">{formErrors.type_name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={laneTypeFormData.description}
                onChange={handleLaneTypeInputChange}
                rows={3}
                className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                placeholder="Enter lane type description (optional)"
                disabled={loading}
              />
            </div>

            {/* Purpose */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Purpose
              </label>
              <input
                type="text"
                name="purpose"
                value={laneTypeFormData.purpose}
                onChange={handleLaneTypeInputChange}
                className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter purpose (e.g., APP, VIP) - optional"
                disabled={loading}
              />
            </div>

            {/* Submit Error */}
            {formErrors.submit && (
              <div className="p-3 bg-red-900/50 border border-red-500 rounded-md">
                <p className="text-sm text-red-400">{formErrors.submit}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                type="submit" 
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                {loading ? (editingItem ? 'Updating...' : 'Creating...') : (editingItem ? 'Update Lane Type' : 'Create Lane Type')}
              </button>
              <button 
                type="button" 
                onClick={resetLaneTypeForm}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lane Types List */}
      <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Lane Types List</h3>
          <div className="text-sm text-gray-400">
            Showing {getFilteredLaneTypes().length} of {Array.isArray(laneTypes) ? laneTypes.filter((lt: LaneType) => lt.is_active).length : 0} lane types
            {selectedPurpose !== 'all' && (
              <span className="ml-2 px-2 py-1 bg-blue-600 text-white text-xs rounded">
                Purpose: {selectedPurpose === 'none' ? 'No Purpose' : selectedPurpose}
              </span>
            )}
          </div>
        </div>
        {loading && !showAddForm ? (
          <div className="text-center py-8">
            <div className="loading-spinner mx-auto mb-2"></div>
            <p className="text-gray-400">Loading lane types...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {getFilteredLaneTypes().map((laneType: LaneType) => (
              <div key={laneType.lane_type_id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-gray-700 rounded space-y-2 sm:space-y-0">
                <div className="flex-1">
                  <div className="font-medium text-white">{laneType.type_name}</div>
                  <div className="text-sm text-gray-400 mt-1">
                    Terminal: {laneType.terminal_name}
                  </div>
                  <div className="text-sm text-gray-400">
                    Checkpoint: {laneType.checkpoint_name}
                  </div>
                  {laneType.description && (
                    <div className="text-sm text-gray-400 mt-1">{laneType.description}</div>
                  )}
                  {laneType.purpose && (
                    <div className="text-sm text-gray-400 mt-1">
                      Purpose: {laneType.purpose}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Created: {new Date(laneType.created_date).toLocaleDateString()}
                  </div>
                </div>
                {permissionChecks.canUpdateQueueTypes() ? (
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Edit Button */}
                    <button 
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                      onClick={() => handleEditLaneType(laneType)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {getFilteredLaneTypes().length === 0 && !loading && (
              <div className="text-center py-8 text-gray-400">
                {selectedPurpose !== 'all' 
                  ? `No lane types found with the selected purpose filter.`
                  : 'No lane types found. Create your first lane type to get started.'
                }
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};